import { ApiTableDataStoreBase, ApiTableDataStoreState } from '@bases/table-data-store/api-table-data-store.base';
import { DocumentRequest } from '@models/document-request';
import { ContainerState, ContainerStateHelper, PaginationData, PaginationRange } from 'tiime-material';
import { Company } from '@models/company';
import { Observable, of } from 'rxjs';
import { Injectable } from '@angular/core';
import { AccountingPeriod } from '@models/accounting-period';
import { switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { tapResponse } from '@ngrx/operators';
import { noop } from '@helpers/noop.helper';
import { AccountingPeriodApiService } from '@api-services/accounting-period-api.service';
import { DocumentRequestsApiService } from '@api-services/document-requests-api.service';
import { DocumentRequestModuleType } from '@enums/document-request-module-type.enum';
import { UntilDestroy } from '@ngneat/until-destroy';
import { DocumentRequestFilters } from '@interfaces/document-request-filters';
import { DocumentApiService } from '@api-services/document-api.service';
import { FileSaverData, FileService } from '@services/file.service';
import { DocumentRequestTemplate } from '@forms/document-request-template';
import {
  lastClosedAccountingPeriodSelector,
  selectedAccountingPeriodInstanceSelector
} from '@modules/company/company-core/store/accounting-periods/accounting-periods-selector';
import { Store } from '@ngrx/store';
import { DateHelper } from '@helpers/date.helper';

export type DocumentRequestPanelMode = 'List' | 'Creation' | 'Detail';

export type DocumentRequestsStoreState = ApiTableDataStoreState<DocumentRequest, DocumentRequestFilters> & {
  opened: boolean;
  company: Company | null;
  mode: DocumentRequestPanelMode;
  accountingPeriods: AccountingPeriod[];
  moduleType: DocumentRequestModuleType | null;
  templates: DocumentRequestTemplate[];
};

export const initialState: DocumentRequestsStoreState = {
  data: [],
  accountingPeriods: [],
  containerState: ContainerState.contentPlaceholder,
  pagination: PaginationRange.withPageSize(100),
  search: null,
  isLoading: false,
  filters: {
    accountingPeriod: null,
    documentRequestStatus: 'all'
  },
  sort: null,
  selected: null,
  opened: false,
  company: null,
  mode: 'List',
  moduleType: null,
  templates: []
};

@UntilDestroy({ checkProperties: true })
@Injectable()
export class DocumentRequestsStore extends ApiTableDataStoreBase<
  DocumentRequest,
  DocumentRequestFilters,
  DocumentRequestsStoreState
> {
  readonly opened$: Observable<boolean> = this.select(({ opened }) => opened);
  readonly accountingPeriods$: Observable<AccountingPeriod[]> = this.select(
    ({ accountingPeriods }) => accountingPeriods
  );
  readonly mode$: Observable<DocumentRequestPanelMode> = this.select(({ mode }) => mode);
  readonly company$: Observable<Company> = this.select(({ company }) => company);
  readonly moduleType$: Observable<DocumentRequestModuleType> = this.select(({ moduleType }) => moduleType);
  readonly selected$: Observable<DocumentRequest | null> = this.select(({ selected }) => selected);
  readonly templates$: Observable<DocumentRequestTemplate[]> = this.select(({ templates }) => templates);
  readonly setOpened = this.updater((state, opened: boolean) => ({
    ...state,
    opened
  }));

  readonly setMode = this.updater((state, mode: DocumentRequestPanelMode) => ({
    ...state,
    mode
  }));

  readonly loadAccountingPeriods = this.effect((company$: Observable<Company>) =>
    company$.pipe(
      tap(() => this.patchState({ accountingPeriods: [] })),
      switchMap(company =>
        this.accountingPeriodApiService
          .getAccountingPeriods(company.id)
          .pipe(tapResponse(accountingPeriods => this.patchState({ accountingPeriods }), noop))
      )
    )
  );

  constructor(
    private readonly documentRequestsApiService: DocumentRequestsApiService,
    private readonly accountingPeriodApiService: AccountingPeriodApiService,
    private readonly documentApiService: DocumentApiService,
    private readonly store: Store,
    private readonly fileService: FileService
  ) {
    super(initialState);
  }

  /**
   * @param company
   * @param moduleType
   * @description if moduleType is specified, open the panel in creation mode with the module type information filled
   */
  open(company: Company, moduleType?: DocumentRequestModuleType): void {
    this.reset();
    this.loadAccountingPeriods(company);

    this.documentRequestsApiService
      .getDocumentRequestTemplates()
      .pipe(
        withLatestFrom(
          this.store.select(selectedAccountingPeriodInstanceSelector),
          this.store.select(lastClosedAccountingPeriodSelector)
        ),
        tap(([templates, selectedAccountingPeriod, lastClosedAccountingPeriod]) => {
          const updatedTemplates = this.updateTemplateWithAccountingPeriods(
            templates,
            selectedAccountingPeriod,
            lastClosedAccountingPeriod
          );
          this.patchState({
            opened: true,
            company,
            moduleType,
            mode: moduleType ? 'Creation' : 'List',
            templates: updatedTemplates
          });
        })
      )
      .subscribe();
  }

  createDocumentRequest(
    documentRequest: Pick<DocumentRequest, 'moduleType' | 'title' | 'description' | 'accountingPeriodId' | 'fileUuid'>,
    file?: File
  ): Observable<DocumentRequest> {
    if (file) {
      return this.documentRequestsApiService.uploadDocumentRequestFile(file).pipe(
        switchMap(fileResponse => {
          documentRequest.fileUuid = fileResponse.uuid;
          return this.documentRequestsApiService.createDocumentRequest(documentRequest);
        })
      );
    }
    return this.documentRequestsApiService.createDocumentRequest(documentRequest);
  }

  downloadDocumentRequestTemplateFile(template: DocumentRequestTemplate): Observable<FileSaverData> {
    return this.documentRequestsApiService.downloadDocumentRequestTemplateFile(template.id);
  }

  downloadCustomerDocument(id: number): Observable<FileSaverData> {
    return this.documentApiService
      .downloadDocument(id)
      .pipe(tap((fileSaverData: FileSaverData) => this.fileService.save(fileSaverData)));
  }

  openCustomerDocumentInNewTab(id: number): void {
    this.documentApiService
      .getDocumentPreview(id)
      .pipe(tap((file: Blob) => this.fileService.openInNewTab(file)))
      .subscribe();
  }

  openOrCloseDocumentRequest(request: DocumentRequest, option: { close: boolean }): Observable<DocumentRequest> {
    return this.documentRequestsApiService.closeDocumentRequest(request, option).pipe(
      tap(updatedRequest => {
        this.forceRefresh();
        this.setSelected(updatedRequest);
      })
    );
  }

  protected override getData(): Observable<PaginationData<DocumentRequest>> {
    const { pagination, search, filters, company } = this.get();

    if (!company) {
      return of({
        data: [],
        paginationRange: PaginationRange.withPageSize(100)
      });
    }

    return this.documentRequestsApiService.getDocumentRequests(pagination, search, filters, company.id).pipe(
      tap(paginationData => {
        this.patchState({
          data: paginationData.data,
          pagination: paginationData.paginationRange,
          containerState: ContainerStateHelper.getContainerState(paginationData.data.length),
          isLoading: false
        });
      })
    );
  }

  private updateTemplateWithAccountingPeriods(
    templates: DocumentRequestTemplate[],
    selectedAccountingPeriod: AccountingPeriod,
    lastClosedAccountingPeriod: AccountingPeriod
  ): DocumentRequestTemplate[] {
    return templates.map(template => {
      if (template.moduleType === DocumentRequestModuleType.TNS && selectedAccountingPeriod) {
        return {
          ...template,
          title: `${template.title} au ${DateHelper.format(selectedAccountingPeriod.endDate, 'DD/MM/yyyy')}`,
          accountingPeriodId: selectedAccountingPeriod.id
        };
      }
      if (template.moduleType === DocumentRequestModuleType.RESULT_ALLOCATION && lastClosedAccountingPeriod) {
        return {
          ...template,
          title: `${template.title} au ${DateHelper.format(lastClosedAccountingPeriod.endDate, 'DD/MM/yyyy')}`,
          accountingPeriodId: selectedAccountingPeriod.id
        };
      }
      return {
        ...template,
        accountingPeriodId: selectedAccountingPeriod.id
      };
    });
  }
}
